import { useEffect, useState } from 'react'
import {
  getBmpLayerIdFromBaseTable,
  getBmpLayerUrl,
  getBmpLayerById,
} from '../../../lib/bmp-layers'

interface TableField {
  name: string
  label: string
  type: 'string' | 'number' | 'boolean' | 'date'
  aggregatable: boolean
  description?: string
}

interface TableInfo {
  tableName: string
  label: string
  description: string
  recordCount: number
  icon: string
  fields: TableField[]
  /** Muestra de registros (BMP: primeros N features; tablas: opcional) */
  sampleRows?: Record<string, unknown>[]
}

interface Props {
  dataSource: string
  componentType: string
  dataSources: any[]
  onClose: () => void
  onSuccess?: () => void
}

export function TableFieldsModal({
  dataSource,
  componentType,
  dataSources,
  onClose,
}: Props) {
  const [tableInfo, setTableInfo] = useState<TableInfo | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (dataSource) {
      loadTableInfo()
    } else {
      setTableInfo(null)
    }
  }, [dataSource, dataSources])

  const inferFieldType = (value: unknown): TableField['type'] => {
    if (value == null) return 'string'
    if (typeof value === 'number' && !Number.isNaN(value)) return 'number'
    if (typeof value === 'boolean') return 'boolean'
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return 'date'
    if (typeof value === 'string' && !Number.isNaN(Number(value))) return 'number'
    return 'string'
  }

  const loadTableInfo = async () => {
    setLoading(true)
    setTableInfo(null)

    // Fuente BMP (capas GeoJSON)
    if (dataSource.startsWith('bmp:')) {
      const layerId = getBmpLayerIdFromBaseTable(dataSource)
      const url = layerId ? getBmpLayerUrl(layerId) : null
      const layerInfo = layerId ? getBmpLayerById(layerId) : null
      if (!url) {
        setLoading(false)
        return
      }
      try {
        const res = await fetch(url)
        const geojson = await res.json()
        const features = geojson?.features ?? []
        const firstProps = features[0]?.properties
        const fieldNames = firstProps ? Object.keys(firstProps) : []
        const fields: TableField[] = fieldNames.map((name) => {
          const val = firstProps[name]
          const type = inferFieldType(val)
          return {
            name,
            label: name,
            type,
            aggregatable: type === 'number',
            description: undefined,
          }
        })
        const sampleRows = features.slice(0, 5).map((f: { properties?: Record<string, unknown> }) => ({ ...(f.properties || {}) }))
        setTableInfo({
          tableName: dataSource,
          label: layerInfo?.displayName ?? dataSource,
          description: `Capa BMP (GeoJSON) · ${features.length} elementos`,
          recordCount: features.length,
          icon: '🗺️',
          fields,
          sampleRows,
        })
      } catch {
        setTableInfo(null)
      }
      setLoading(false)
      return
    }

    // Tablas BD: buscar en dataSources
    const foundTable = dataSources.find(ds => ds.tableName === dataSource)
    if (foundTable) {
      setTableInfo({ ...foundTable, sampleRows: foundTable.sampleRows })
    } else {
      setTableInfo(null)
    }
    setLoading(false)
  }

  // Función para traducir tipos de datos
  const translateDataType = (type: string): string => {
    const typeMap: Record<string, string> = {
      'number': 'Número',
      'string': 'Texto',
      'boolean': 'Booleano',
      'date': 'Fecha',
      'integer': 'Entero',
      'bigint': 'Entero grande',
      'smallint': 'Entero pequeño',
      'numeric': 'Numérico',
      'real': 'Real',
      'double precision': 'Doble precisión',
      'decimal': 'Decimal',
      'character varying': 'Texto',
      'varchar': 'Texto',
      'text': 'Texto largo',
      'character': 'Carácter',
      'char': 'Carácter',
      'timestamp': 'Fecha y hora',
      'timestamp with time zone': 'Fecha y hora con zona',
      'time': 'Hora',
      'time with time zone': 'Hora con zona',
      'json': 'JSON',
      'jsonb': 'JSON binario',
      'uuid': 'UUID',
      'bytea': 'Binario',
    }

    return typeMap[type] || type
  }

  // Determinar uso recomendado del campo basado en tipo y agregabilidad
  const getFieldUsage = (field: TableField): string => {
    const { type, aggregatable } = field
    
    if (aggregatable && type === 'number') {
      return 'Valor numérico (ejes Y, KPIs, cálculos)'
    } else if (type === 'string') {
      return 'Texto (etiquetas, categorías, ejes X)'
    } else if (type === 'number' && !aggregatable) {
      // Números no agregables suelen ser IDs o códigos
      return 'Identificador o código (filtros, relaciones)'
    } else if (type === 'date' || type.includes('timestamp')) {
      return 'Fecha/Hora (series temporales)'
    } else if (type === 'boolean') {
      return 'Booleano (filtros, estados)'
    }
    
    return 'Dato general'
  }

  // Obtener icono para tipo de campo
  const getFieldTypeIcon = (type: string): string => {
    if (type === 'number') return '🔢'
    if (type === 'string') return '📝'
    if (type === 'boolean') return '✅'
    if (type === 'date' || type.includes('timestamp')) return '📅'
    return '📋'
  }

  // Obtener color para tipo de campo
  const getFieldTypeColor = (type: string, aggregatable: boolean): string => {
    if (type === 'number' && aggregatable) return 'bg-green-100 text-green-800'
    if (type === 'number' && !aggregatable) return 'bg-purple-100 text-purple-800'
    if (type === 'string') return 'bg-blue-100 text-blue-800'
    if (type === 'boolean') return 'bg-yellow-100 text-yellow-800'
    if (type === 'date') return 'bg-pink-100 text-pink-800'
    return 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto space-y-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              {tableInfo?.icon || '📋'}
              <span>{tableInfo?.label || dataSource}</span>
            </h2>
            <p className="text-gray-600 mt-1">
              {tableInfo?.description || `Campos disponibles en la tabla ${dataSource}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Información de la tabla */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500">Tabla</div>
            <div className="font-mono text-lg font-medium">{dataSource}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500">Registros</div>
            <div className="text-lg font-medium">
              {tableInfo?.recordCount ? tableInfo.recordCount.toLocaleString() : 'N/A'}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-500">Tipo de Componente</div>
            <div className="text-lg font-medium text-green-600">{componentType}</div>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
            <p className="text-gray-600">Cargando campos...</p>
          </div>
        ) : tableInfo && tableInfo.fields.length > 0 ? (
          <>
            <div className="border rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Campo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descripción
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uso Recomendado
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tableInfo.fields.map((field, index) => (
                    <tr key={field.name} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span>{getFieldTypeIcon(field.type)}</span>
                          <div>
                            <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded block">
                              {field.name}
                            </code>
                            <div className="text-xs text-gray-500 mt-1">{field.label}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${getFieldTypeColor(field.type, field.aggregatable)}`}>
                          {translateDataType(field.type)}
                        </span>
                      </td>
                      
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-600 max-w-xs">
                          {field.description || '—'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {getFieldUsage(field)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Muestra de registros (BMP o tablas con sampleRows) */}
            {tableInfo.sampleRows && tableInfo.sampleRows.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <h3 className="px-4 py-2 bg-gray-100 font-medium text-sm text-gray-700">
                  Muestra de registros ({tableInfo.sampleRows.length})
                </h3>
                <div className="overflow-x-auto max-h-48 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        {tableInfo.fields.map((f) => (
                          <th key={f.name} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {f.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tableInfo.sampleRows.map((row, i) => (
                        <tr key={i}>
                          {tableInfo.fields.map((f) => (
                            <td key={f.name} className="px-4 py-2 text-gray-700">
                              {row[f.name] != null ? String(row[f.name]) : '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Leyenda de tipos */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-800 mb-3">📊 Leyenda de Tipos de Campos</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                    Número Agregable
                  </span>
                  <span className="text-xs text-gray-600">Suma, promedio, etc.</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                    Número No Agregable
                  </span>
                  <span className="text-xs text-gray-600">IDs, códigos</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                    Texto
                  </span>
                  <span className="text-xs text-gray-600">Etiquetas, categorías</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 text-xs rounded-full bg-pink-100 text-pink-800">
                    Fecha
                  </span>
                  <span className="text-xs text-gray-600">Series temporales</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-gray-500 mb-2">No se encontraron campos para esta tabla.</p>
            <p className="text-sm text-gray-400">
              La tabla "{dataSource}" no está configurada en el sistema o no tiene campos definidos.
            </p>
          </div>
        )}

        {/* Consejos para el tipo de componente */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="font-medium text-green-800 mb-2">🎯 Consejos para {componentType}</h3>
          <ul className="text-sm text-green-700 space-y-1">
            {componentType === 'bar-chart' && (
              <>
                <li>• Usa campos de <strong>texto</strong> para el eje X (categorías)</li>
                <li>• Usa campos <strong>numéricos agregables</strong> para el eje Y (valores)</li>
                <li>• Los campos numéricos no agregables pueden servir para colores o agrupaciones</li>
              </>
            )}
            {componentType === 'line-chart' && (
              <>
                <li>• Usa campos de <strong>fecha</strong> para el eje X (serie temporal)</li>
                <li>• Usa campos <strong>numéricos agregables</strong> para el eje Y</li>
                <li>• Puedes usar campos de texto para agrupar series por categorías</li>
              </>
            )}
            {componentType === 'scatter-plot' && (
              <>
                <li>• Necesitas 2-3 campos <strong>numéricos agregables</strong></li>
                <li>• Usa un campo numérico para el eje X, otro para el eje Y</li>
                <li>• Un tercer campo numérico puede definir el tamaño de los puntos</li>
                <li>• Usa campos de texto para colorear los puntos por categoría</li>
              </>
            )}
            {componentType === 'pie-chart' && (
              <>
                <li>• Usa un campo de <strong>texto</strong> para las categorías</li>
                <li>• Usa un campo <strong>numérico agregable</strong> para los valores</li>
                <li>• El campo numérico determinará el tamaño de cada porción</li>
              </>
            )}
            {componentType === 'heatmap' && (
              <>
                <li>• Usa 2 campos de <strong>texto</strong> para los ejes X e Y</li>
                <li>• Usa un campo <strong>numérico agregable</strong> para los valores/intensidad</li>
                <li>• Los campos deben permitir agrupación por categorías</li>
              </>
            )}
            {componentType === 'kpi' && (
              <>
                <li>• Usa un campo <strong>numérico agregable</strong> para el valor</li>
                <li>• Puedes agregar filtros con campos de texto o números no agregables</li>
              </>
            )}
          </ul>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  )
}